import { armorSummary, classOf, classSkills, OATH_BY_ID, OATH_SLOTS, OATHS, parseOaths, setSprintMul, SHAPE_GLYPHS, simpatiaEffects, ULT_CHARGE, withClass, type SkillDef } from "@eldrun/content";
import { MOVE, mitigate, STAMINA } from "@eldrun/sim";
import type { KitIcon } from "../kit/kit-model.ts";
import type { StatsLike } from "../screens/end-model.ts";

/**
 * Ficha da classe (F82, tecla C; mockup `eldrun-ui-e.png` adaptado): perfil, retrato pela forma da classe,
 * atributos que existem no jogo (defesa das peças e da classe, velocidade, afinidade de arma, Vida, Égide,
 * Fome, Fôlego), a passiva, a ativa e a ultimate com recarga e carga, as estatísticas da partida (as
 * mesmas da tela de fim, ao vivo) e a fantasia da classe. Só dados reais; projeção pura.
 */

export interface SheetInput {
  name: string;
  classId: string;
  /** Slot do jogador (0 a 3) e a cor dele como aparece na tela (o modo daltônico troca). */
  slot: number;
  color: string;
  /** Na Arena os bônus de combate da classe valem a metade (F71). */
  mode: string;
  vitals: { hp: number; hpMax: number; patua: number; patuaMax: number; hunger: number; hungerMax: number } | undefined;
  /** O Fôlego do corpo previsto. */
  stamina: { value: number; max: number } | undefined;
  simpatias: Iterable<[string, number]>;
  /** As peças de armadura vestidas (ids, vazio = nada). */
  armor: readonly string[];
  oaths: string;
  ultCharge: number;
  /** Segundos que faltam para a ativa (0 = pronta). */
  activeLeft: number;
  keys: { ativa: string; ultimate: string };
  /** Estatísticas da partida (públicas no estado); sem elas, a seção sai. */
  stats: StatsLike | undefined;
}

export interface SheetTile {
  id: string;
  icon: KitIcon;
  label: string;
  value: string;
  /** Linha pequena embaixo (o que o número quer dizer). */
  hint: string;
}

export interface SheetBar {
  id: string;
  icon: KitIcon | undefined;
  label: string;
  value: number;
  max: number;
  text: string;
}

export interface SheetSkill {
  id: string;
  kind: string;
  name: string;
  description: string;
  key: string;
  /** Juramentos feitos na habilidade e quantos ela tem (os pips). */
  level: number;
  levels: number;
  /** "Recarga 12 s · pronta", "Carga 64 / 100" ou "Sempre ativa". */
  meta: string;
}

export interface SheetView {
  name: string;
  className: string;
  role: string;
  glyph: string;
  slotN: number;
  color: string;
  profile: [string, string][];
  tiles: SheetTile[];
  bars: SheetBar[];
  skills: SheetSkill[];
  stats: [string, string][] | undefined;
  biography: string[];
}

/** Famílias de arma pelo nome (a afinidade da classe). */
export const WEAPON_FAMILY_NAMES: Readonly<Record<string, string>> = { clava: "Clavas", lanca: "Lanças", lamina: "Lâminas", arco: "Arcos", cajado: "Cajados" };

const num = (v: number, digits = 1): string => v.toFixed(digits).replace(".", ",");
/** Porcentagem com uma casa só quando precisa (15 %, 7,5 %). */
const pct = (v: number): string => `${String(Math.round(v * 1000) / 10).replace(".", ",")} %`;

function skillMeta(skill: SkillDef, kind: "passiva" | "ativa" | "ultimate", taken: readonly string[], input: SheetInput): string {
  if (kind === "passiva") return "Sempre ativa";
  if (kind === "ultimate") {
    const charge = Math.max(0, Math.min(ULT_CHARGE.max, Math.round(input.ultCharge)));
    return charge >= ULT_CHARGE.max ? `Carga ${charge} / ${ULT_CHARGE.max} · pronta` : `Carga ${charge} / ${ULT_CHARGE.max}`;
  }
  // A recarga com os juramentos (nunca abaixo de 1 s, F70).
  const add = taken.reduce((sum, id) => {
    const o = OATH_BY_ID.get(id);
    return o?.skill === skill.id && o.param === "cooldownMs" ? sum + o.add : sum;
  }, 0);
  const total = Math.max(1000, (skill.cooldownMs ?? 0) + add) / 1000;
  const left = Math.ceil(Math.max(0, input.activeLeft));
  return `Recarga ${num(total, total % 1 ? 1 : 0)} s · ${left > 0 ? `faltam ${left} s` : "pronta"}`;
}

export function sheetView(input: SheetInput): SheetView {
  const c = classOf(input.classId);
  const s = classSkills(c);
  const counts = new Map(input.simpatias);
  const effects = withClass(simpatiaEffects((id) => counts.get(id) ?? 0), c.id, input.mode === "treta");
  const armor = armorSummary(input.armor);
  const defense = armor.defense + effects.defense;
  const walk = MOVE.walkSpeed * effects.speedMul;
  const sprint = MOVE.sprintSpeed * effects.speedMul * setSprintMul(armor.set);
  const affinityBonus = effects.affinityMul - 1;
  const family = WEAPON_FAMILY_NAMES[c.affinity] ?? c.affinity;
  const taken = parseOaths(input.oaths);
  const blessings = [...counts.values()].filter((n) => n > 0).reduce((a, n) => a + n, 0);
  const v = input.vitals;
  const staminaMax = input.stamina?.max ?? STAMINA.max + effects.staminaMaxBonus;
  const level = (k: SkillDef) => ({ level: taken.filter((id) => OATH_BY_ID.get(id)?.skill === k.id).length, levels: OATHS.filter((o) => o.skill === k.id).length });
  const skill = (k: SkillDef, kind: "passiva" | "ativa" | "ultimate", label: string, key: string): SheetSkill => ({ id: k.id, kind: label, name: k.name, description: k.description, key, ...level(k), meta: skillMeta(k, kind, taken, input) });
  const st = input.stats;
  return {
    name: input.name || "Sem nome",
    className: c.name,
    role: c.role,
    glyph: SHAPE_GLYPHS[c.shape],
    slotN: input.slot + 1,
    color: input.color,
    profile: [
      ["Classe", c.name],
      ["Papel", c.role],
      ["Jogador", `${input.slot + 1}`],
      ["Afinidade", family],
      ["Juramentos", `${taken.length} / ${OATH_SLOTS}`],
    ],
    tiles: [
      { id: "defesa", icon: "icone-defesa", label: "Defesa", value: String(Math.round(defense)), hint: `−${pct(1 - mitigate(1, defense))} de dano` },
      { id: "velocidade", icon: "icone-velocidade", label: "Velocidade", value: `${num(walk)} m/s`, hint: `correndo ${num(sprint)} m/s` },
      { id: "afinidade", icon: "icone-mira", label: "Afinidade", value: `+${pct(affinityBonus)}`, hint: `de dano com ${family.toLowerCase()}` },
      { id: "bencaos", icon: "aba-bencaos", label: "Bênçãos", value: String(blessings), hint: blessings === 1 ? "pilha" : "pilhas" },
      { id: "juramentos", icon: "icone-talentos", label: "Juramentos", value: `${taken.length}/${OATH_SLOTS}`, hint: "da Alvorada" },
      { id: "ultimate", icon: "aba-habilidades", label: "Ultimate", value: pct(Math.min(1, Math.max(0, input.ultCharge) / ULT_CHARGE.max)), hint: "de carga" },
    ],
    bars: [
      { id: "vida", icon: "vital-vida", label: "Vida", value: Math.round(v?.hp ?? 0), max: v?.hpMax ?? 0, text: `${Math.round(v?.hp ?? 0)} / ${Math.round(v?.hpMax ?? 0)}` },
      { id: "egide", icon: "icone-nivel", label: "Égide", value: Math.round(v?.patua ?? 0), max: v?.patuaMax ?? 0, text: `${Math.round(v?.patua ?? 0)} / ${Math.round(v?.patuaMax ?? 0)}` },
      { id: "fome", icon: "vital-fome", label: "Fome", value: Math.round(v?.hunger ?? 0), max: v?.hungerMax ?? 0, text: `${Math.round(v?.hunger ?? 0)} / ${Math.round(v?.hungerMax ?? 0)}` },
      { id: "folego", icon: "vital-folego", label: "Fôlego", value: Math.round(input.stamina?.value ?? staminaMax), max: staminaMax, text: `${Math.round(input.stamina?.value ?? staminaMax)} / ${Math.round(staminaMax)}` },
    ],
    skills: [skill(s.passiva, "passiva", "Passiva", ""), skill(s.ativa, "ativa", "Ativa", input.keys.ativa), skill(s.ultimate, "ultimate", "Ultimate", input.keys.ultimate)],
    stats: st && [
      ["Abates", String(st.kills)],
      ["Mortes", String(st.deaths)],
      ["Revividas", String(st.revives)],
      ["Dano causado", String(st.dealt)],
      ["Dano recebido", String(st.taken)],
      ["Bloqueado", String(st.blocked ?? 0)],
      ["Cura dada", String(st.healed ?? 0)],
      ["Elos", String(st.elos ?? 0)],
      ["Baús", String(st.chests)],
      ["Moedas", String(st.coins)],
    ],
    biography: [c.fantasy, `${c.perk} Com ${family.toLowerCase()}, +${pct(affinityBonus)} de dano; a classe não proíbe nenhuma arma.`],
  };
}
